import api, { publicApi, formatApiError } from './api';
import { toast } from 'sonner';
import { devLog, devError } from './logger';

// ---------------------------------------------------------------------------
// Cache-aware PDF downloader with background-job awareness.
//
// Contract with backend (server.py):
//   GET /catalogs/{id}/pdf         (authed)   → 200 application/pdf when cached,
//                                              202 JSON {status, poll_url, job} when not.
//   GET /shared/{token}/pdf        (public)   → same contract.
//   GET …/pdf-status                          → {pdf_ready, job:{status,duration_s,error}}
//
// This module:
//   • requests the download once,
//   • on 200 → streams the PDF into a browser download,
//   • on 202 → shows a "Preparing PDF…" toast, polls status every 2s, then
//             auto-retries the download as soon as pdf_ready=true.
// The whole flow times out after PDF_DOWNLOAD_MAX_MS to avoid leaking
// pending toasts if something is genuinely stuck server-side.
// ---------------------------------------------------------------------------

const PDF_DOWNLOAD_MAX_MS = 5 * 60_000;   // 5 min ceiling for background generation
const PDF_POLL_INTERVAL_MS = 2_000;

const isMobile = () => /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent);

function _triggerBrowserDownload(blob, title) {
  const objectUrl = URL.createObjectURL(blob);
  // Mobile / in-app browsers (WhatsApp, IG, Messenger) block auto-download
  // attribute behaviour — open the PDF in a new tab instead so the user can
  // tap "Save" / "Share" from their native viewer.
  if (isMobile()) {
    window.open(objectUrl, '_blank');
  } else {
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = `${title || 'catalog'}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}

async function _attemptDownload(client, url) {
  // axios validateStatus accepts 200 (cached) and 202 (job started). All other
  // statuses still throw so we can surface real errors. We ask for an
  // arraybuffer response — that lets us read the 202 JSON body manually
  // without forcing two separate requests.
  const response = await client.get(url, {
    responseType: 'arraybuffer',
    timeout: 30_000,  // short timeout — backend never blocks >30s now
    validateStatus: (s) => s === 200 || s === 202,
  });
  const status = response.status;
  const cacheHeader = response.headers?.['x-pdf-cache'];
  const contentType = response.headers?.['content-type'] || '';
  if (status === 200 && contentType.includes('pdf')) {
    return { kind: 'pdf', blob: new Blob([response.data], { type: 'application/pdf' }), cached: cacheHeader === 'hit' };
  }
  // 202 — body is JSON describing the in-flight job.
  try {
    const text = new TextDecoder().decode(response.data);
    const json = JSON.parse(text);
    return { kind: 'preparing', payload: json };
  } catch (e) {
    return { kind: 'preparing', payload: { status: 'preparing' } };
  }
}

async function _pollUntilReady(client, statusUrl, deadlineMs, toastId) {
  // Returns true when pdf_ready=true. Throws if the job fails or the
  // overall deadline lapses (e.g. >5 min). Updates the toast on every tick
  // so the user sees real progress instead of a frozen spinner.
  while (Date.now() < deadlineMs) {
    await new Promise(r => setTimeout(r, PDF_POLL_INTERVAL_MS));
    let s;
    try { s = (await client.get(statusUrl, { timeout: 10_000 })).data; }
    catch (e) { devError('[PDF] status poll error', e); continue; }
    const job = s?.job || {};
    if (s?.pdf_ready) return true;
    if (job.status === 'failed') {
      throw new Error(job.error || 'PDF generation failed');
    }
    const STATUS_LABELS = { generating: 'Generating PDF…', queued: 'Queued — waiting for a worker…' };
    const label = STATUS_LABELS[job.status] || 'Preparing PDF…';
    toast.loading(label, { id: toastId });
  }
  throw new Error('PDF generation timed out after 5 minutes');
}

/**
 * Download a catalog PDF. Returns {ok, cached}. Handles the full
 * 202-prepare-poll-retry flow transparently — callers only see a single
 * promise that resolves when the download has been triggered (or rejects
 * with a clear toast on failure).
 *
 * @param {string} url       API path (e.g. '/catalogs/abc/pdf' or '/shared/tok/pdf')
 * @param {string} title     File title (used for download filename)
 * @param {object} [options] {publicRoute: true} for /shared/* downloads — uses the
 *                           no-credentials axios so mobile in-app browsers don't block
 */
export async function downloadPdfWithCache(url, title, options = {}) {
  const toastId = toast.loading('Preparing PDF…');
  const client = options.publicRoute ? publicApi : api;
  const deadline = Date.now() + PDF_DOWNLOAD_MAX_MS;
  try {
    // First attempt — if cached, this returns 200 in <500ms.
    let attempt = await _attemptDownload(client, url);
    if (attempt.kind === 'preparing') {
      devLog(`[PDF] ${url} → 202 (preparing in background)`);
      const job = attempt.payload?.job || {};
      toast.loading(job.status === 'generating' ? 'Generating PDF…' : 'Preparing PDF…', { id: toastId });
      // Derive status URL from the catalog_id the backend returned. Falls
      // back to swapping /pdf → /pdf-status if the backend didn't include
      // poll_url (older deployments).
      let statusUrl = attempt.payload?.poll_url || url.replace(/\/pdf$/, '/pdf-status');
      if (statusUrl.startsWith('/api/')) statusUrl = statusUrl.slice(4);  // axios baseURL is .../api
      await _pollUntilReady(client, statusUrl, deadline, toastId);
      // Cache should be warm now — retry the download once.
      attempt = await _attemptDownload(client, url);
      if (attempt.kind !== 'pdf') throw new Error('PDF still not ready after generation');
    }
    _triggerBrowserDownload(attempt.blob, title);
    toast.success(attempt.cached ? 'PDF ready (cached)' : 'PDF ready', { id: toastId });
    return { ok: true, cached: attempt.cached };
  } catch (err) {
    devError(`[PDF] ${url} download failed`, err);
    const msg = err.response?.status === 404 ? 'Catalog not found'
              : err.message?.includes('timed out') ? 'PDF generation timed out — please retry'
              : err.message?.includes('failed') ? err.message
              : formatApiError(err.response?.data?.detail) || 'Failed to download PDF';
    toast.error(msg, { id: toastId });
    return { ok: false, cached: false };
  }
}
